<script setup lang="ts">
import { IonButton, IonContent, IonFooter, IonIcon, IonRefresher, IonRefresherContent, RefresherCustomEvent, IonSpinner } from '@ionic/vue';
import { checkmarkCircle, time, warning } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useSafety, { Incident, IncidentStatus } from '@/modules/safety/composables/useSafety';
import BackButton from '@/shared/components/BackButton.vue';

const { t } = useI18n();
const router = useRouter();
const { getMyReports } = useSafety();
const { reports, isFetchingReports, refetchReports } = getMyReports();

const onRefresh = async (ev: RefresherCustomEvent) => {
	await refetchReports();
	ev.target.complete();
};

const goToDetail = (r: Incident) => {
	router.push({ name: 'safety-my-report-detail', params: { id: r.id } });
};

const statusStyle = (status: IncidentStatus): string => {
	switch (status) {
		case 'resolved':
			return 'bg-green-600/20 text-green-300';
		case 'cancelled':
			return 'bg-gray-500/20 text-gray-300';
		case 'in_progress':
			return 'bg-yellow-500/20 text-yellow-200';
		case 'assigned':
			return 'bg-blue-500/20 text-blue-200';
		default:
			return 'bg-red-500/20 text-red-200';
	}
};

const statusIcon = (status: IncidentStatus) => {
	if (status === 'resolved' || status === 'cancelled') return checkmarkCircle;
	if (status === 'in_progress' || status === 'assigned') return time;
	return warning;
};

const formatDate = (iso: string) => new Date(iso).toLocaleString();
</script>

<template>
	<ion-content>
		<ion-refresher class="mt-8" slot="fixed" @ionRefresh="onRefresh">
			<ion-refresher-content />
		</ion-refresher>

		<TopBar class="px-[34px]">
			<template #left>
				<BackButton to="/safety" />
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t('safety.my_reports') }}
				</div>
			</template>
		</TopBar>

		<div class="px-[34px] pb-[120px] w-full max-w-[600px] mx-auto">
			<div v-if="isFetchingReports && !reports.length" class="flex justify-center py-10">
				<ion-spinner name="crescent" />
			</div>

			<div v-else-if="!reports.length" class="text-center text-white/70 py-10 font-sourcesanspro-regular">
				{{ t('safety.no_reports_yet') }}
			</div>

			<div v-else class="space-y-2">
				<button v-for="r in reports" :key="r.id" class="w-full min-h-[80px] rounded backdrop-blur-xl bg-white/10 text-white p-4 flex items-center gap-3 active:bg-white/20 text-left" @click="goToDetail(r)">
					<ion-icon :icon="statusIcon(r.status)" class="text-l2 shrink-0" />
					<div class="flex-1 min-w-0">
						<div class="flex items-center gap-2">
							<span class="font-sourcesanspro-semibold text-base2 truncate">{{ t(`safety.types.${r.type}`) }}</span>
							<span class="text-xs2 font-mono opacity-60 shrink-0">#{{ r.public_code }}</span>
						</div>
						<div class="text-white/60 text-xs2 font-sourcesanspro-regular">{{ formatDate(r.created_at) }}</div>
					</div>
					<span class="text-xs2 uppercase font-sourcesanspro-semibold px-2 py-1 rounded shrink-0" :class="statusStyle(r.status)">
						{{ t(`safety.status.${r.status}`) }}
					</span>
				</button>
			</div>
		</div>
	</ion-content>

	<ion-footer>
		<NavigationBar item-active="safety" />
	</ion-footer>
</template>
